/**
 * Klien kanal dorong — `WebSocket` bawaan peramban, tanpa pustaka.
 *
 * ### ⭐ Kenapa `ws` polos, bukan socket.io
 * Karena yang lewat kanal ini cuma **dentuman** (`@osbpos/contract` `live.ts`): mesin pengiriman
 * socket.io baru berharga bila pesannya membawa isi. Rinciannya + fallback-nya dibahas di
 * `CHANGELOG`; yang penting di sini: pesan yang hilang paling mahal menunda satu pembacaan.
 *
 * ### ⛔⛔ Degradasi WAJIB TERLIHAT
 * Justru inilah yang **tak** saya ambil dari socket.io. Fallback diam-diam ke long-polling membuat
 * dapur bekerja di atas jaringan rusak sambil merasa sehat — dan yang pertama tahu ada yang salah
 * adalah tamu yang makanannya tak datang. Di sini kemunduran dilaporkan lewat `onState`, supaya
 * layar bisa mengatakannya kepada **manusia di ruangan itu**, bukan cuma ke log.
 */
import { LIVE_FALLBACK_POLL_MS, isLiveKind, type LiveKind } from '@osbpos/contract';

/** Sehat = tersambung & sudah `ready`. `degraded` = jaring poll yang lagi nanggung. */
export type LiveState = 'connecting' | 'live' | 'degraded';

export interface LiveOptions {
  readonly outletCode: string;
  readonly staffToken: string;
  /** Dipanggil tiap ada dentuman — dan juga oleh jaring poll, dengan `undefined` = "baca semua". */
  readonly onNudge: (kind: LiveKind | undefined) => void;
  readonly onState: (state: LiveState) => void;
}

/**
 * ⚠️ Jeda coba-sambung-ulang naik bertahap, **berhenti di 15 detik**. Tanpa plafon, kanal yang
 * putus semalam butuh berjam-jam untuk pulih sesudah jaringan kembali — dan yang menyalakan Wi-Fi
 * pagi-pagi berhak melihat layarnya hidup lagi dalam hitungan detik.
 */
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 15_000] as const;

/**
 * Poll saat kanal SEDANG RUSAK. ⛔ Jauh lebih rapat dari {@link LIVE_FALLBACK_POLL_MS}: yang
 * terakhir itu safetyNet saat semuanya sehat, yang ini pengganti kanal yang sedang mati.
 */
const EMERGENCY_POLL_MS = 5_000;

export interface LiveChannel {
  readonly close: () => void;
}

export function openLive(opts: LiveOptions): LiveChannel {
  let ws: WebSocket | undefined;
  let failures = 0;
  let closed = false;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let safetyNet: ReturnType<typeof setInterval> | undefined;

  const url = (): string => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/api/live`;
  };

  const armSafetyNet = (ms: number): void => {
    if (safetyNet !== undefined) clearInterval(safetyNet);
    safetyNet = setInterval(() => opts.onNudge(undefined), ms);
  };

  const connect = (): void => {
    if (closed) return;
    opts.onState(failures === 0 ? 'connecting' : 'degraded');
    let socket: WebSocket;
    try {
      socket = new WebSocket(url());
    } catch {
      scheduleRetry();
      return;
    }
    ws = socket;

    socket.onopen = () => {
      /* ⛔ Token dikirim di PESAN, tak pernah di URL — URL mendarat di log akses & log proxy. */
      socket.send(JSON.stringify({
        type: 'hello', outletCode: opts.outletCode, staffToken: opts.staffToken,
      }));
    };

    socket.onmessage = (ev) => {
      let msg: { type?: string; kind?: unknown };
      try {
        msg = JSON.parse(String(ev.data)) as { type?: string; kind?: unknown };
      } catch {
        return;
      }
      if (msg.type === 'ready') {
        failures = 0;
        opts.onState('live');
        /*
         * ⚠️ Sekali baca ulang begitu tersambung. Selama kanal putus, hal-hal terjadi tanpa
         * terdengar — dan layar yang menyambung lagi lalu menampilkan keadaan lama adalah layar
         * yang berbohong dengan sangat meyakinkan.
         */
        opts.onNudge(undefined);
        armSafetyNet(LIVE_FALLBACK_POLL_MS);
        return;
      }
      if (msg.type === 'nudge' && isLiveKind(msg.kind)) opts.onNudge(msg.kind);
    };

    socket.onclose = () => {
      if (closed) return;
      scheduleRetry();
    };
    socket.onerror = () => socket.close();
  };

  const scheduleRetry = (): void => {
    if (closed) return;
    opts.onState('degraded');
    // ⛔ Jaring dipercepat: selama kanal mati, dialah satu-satunya yang menggerakkan layar.
    armSafetyNet(EMERGENCY_POLL_MS);
    const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)] ?? 15_000;
    failures += 1;
    if (retryTimer !== undefined) clearTimeout(retryTimer);
    retryTimer = setTimeout(connect, delay);
  };

  armSafetyNet(EMERGENCY_POLL_MS); // sampai `ready` datang, anggap belum ada kanal
  connect();

  return {
    close: () => {
      closed = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      if (safetyNet !== undefined) clearInterval(safetyNet);
      ws?.close();
    },
  };
}
